import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

import type { StrategySeed } from './DockMenu';
import {
	fieldForStep,
	fieldsBeforeStep,
	fieldsFromStep,
	GROUNDING_FIELDS,
	isNorthStarStep,
	type GroundingAnswers,
	type GroundingCorrectionId,
	type GroundingFieldId,
	NORTH_STAR_PROSE_KEY,
	NORTH_STAR_STEP,
	NORTH_STAR_VALUE_KEY,
	NORTH_STAR_WORDS_KEY,
	TOTAL_STEPS,
} from './groundingFields';
import {
	composeGroundingMarkdown,
	parseGroundingMarkdown,
	stripHintFromAnswer,
} from './groundingMarkdown';
import {
	declinesNorthStar,
	figureNamesAPeriod,
	formatNorthStar,
	type NorthStar,
	parseNorthStarAnswer,
	parseNorthStarValue,
	toNorthStarValue,
} from './northStar';

// State for the guided grounding capture. Owns the answers, the step cursor, the exit-animation
// hold, and the persist seam.
//
// The persist rule is the load-bearing part. The server derives `edited` from row existence, and
// the strategy graph only feeds the brief to the strategist when `edited` is true — a gate that
// exists so our own machine-written seed is never presented as the owner's words. So a write must
// happen ONLY once an answer actually differs from the seeded baseline. Reading the pre-filled
// guesses and pressing Enter through them writes nothing at all.

export type StepIndex = number;
export const REVIEW_STEP = TOTAL_STEPS;

/** What a step records when it is answered: a field id, or the number's own key. */
const stepId = (step: number): GroundingCorrectionId =>
	fieldForStep(step)?.id ?? NORTH_STAR_VALUE_KEY;

type Corrections = Partial<Record<GroundingCorrectionId, string>>;

/**
 * The confirmed number as one comparable string, for checking what is on screen against what the
 * server has acknowledged.
 *
 * The owner's words are part of it: changing only those leaves the wire value identical, so
 * comparing that alone would call an unsaved edit saved. Both sides go through the same parse
 * first, so a stored value written with stray spacing still compares equal to the same number on
 * screen — a warning that fires when nothing is wrong is as bad as a missing one.
 */
const northStarStamp = (value: string, words: string): string => {
	const star = parseNorthStarValue(value);
	return star ? `${toNorthStarValue(star)}|${words.trim()}` : '';
};

/** Whether two correction sets hold the same values, so a Save that changed nothing writes nothing. */
const sameCorrections = (a: Corrections, b: Corrections): boolean => {
	const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
	for (const k of keys) {
		const key = k as GroundingCorrectionId;
		if ((a[key] ?? '') !== (b[key] ?? '')) return false;
	}
	return true;
};

/**
 * Drop any correction that is only one of our own coaching hints.
 *
 * Stored corrections outrank the brief, so a hint committed before it was strippable would be
 * offered back as the owner's answer forever. Both the live and the saved set are cleaned the same
 * way, so healing a poisoned doc never looks like a change and never triggers a write (ADO-1983).
 */
const withoutHints = (corrections: Corrections): Corrections => {
	const out: Corrections = {};
	for (const [k, v] of Object.entries(corrections)) {
		// The key is passed so a RETIRED question's hint is reachable too. That hint is keyed by the
		// question that replaced it, and it is not in the live set the value-only strip searches.
		const kept = stripHintFromAnswer(v ?? '', k as GroundingCorrectionId);
		if (kept) out[k as GroundingCorrectionId] = kept;
	}
	return out;
};

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface ContextResponse {
	markdown: string;
	/** Field id → what the owner told us last time. Beats anything we re-inferred. */
	corrections?: Record<string, string>;
	updatedAt: string | null;
	edited: boolean;
}

export interface InterviewTurn {
	question: string;
	suggestions: string[];
	grounded: boolean;
}

/** What the owner's free-text answer did, as read by the server. */
export type ReplyVerdict = 'answered' | 'question' | 'unclear' | 'declined';

export interface GroundingReply {
	verdict: ReplyVerdict;
	/** Every fact their answer stated — often more than the one we asked about. */
	facts: Record<string, string>;
	reply: string;
	suggestions: string[];
	judged: boolean;
	/**
	 * The owner left this fact before we finished reading their answer. The card must then do
	 * NOTHING — not advance, and not offer follow-up options, which would replace the choices of
	 * the step they moved to with the ones belonging to the step they left.
	 */
	abandoned?: boolean;
}

/**
 * One thing said, in the order it was said.
 *
 * The transcript used to be DERIVED from the field list plus the answers, which can only ever
 * describe a form being filled in order. A conversation has things a derivation cannot produce: a
 * follow-up on a fact we already asked about, our answer to a question the owner asked us, an
 * acknowledgement of two facts given in one breath. So it is recorded, not computed.
 */
export interface ConversationEntry {
	id: string;
	role: 'agent' | 'owner';
	text: string;
	/** The fact under discussion, for the ones that belong to a field. */
	fieldId?: GroundingCorrectionId;
}

export interface GroundingFlowState {
	loading: boolean;
	loadError: boolean;
	saveState: SaveState;
	answers: GroundingAnswers;
	/** The values we inferred, used to decide what counts as a real edit. */
	seedAnswers: GroundingAnswers;
	stepIndex: StepIndex;
	direction: 'forward' | 'back';
	/** The step still on screen playing its exit, or null. */
	exitingStep: StepIndex | null;
	extra: string;
	dirty: boolean;
	/** How many facts the OWNER has answered. Filled-by-us fields do not count. */
	answeredByOwner: number;
	/** The LLM-composed turn for the current question, or null while it is being written. */
	turn: InterviewTurn | null;
	turnLoading: boolean;
	/** Everything said so far, in order. */
	conversation: ConversationEntry[];
	/** True while the owner's typed answer is being read. */
	replyPending: boolean;
	/** The number the strategy will be judged on, once they have confirmed one. */
	northStar: NorthStar | null;
	/**
	 * The row is showing a confirmation the server has not taken. The row renders the moment they
	 * press Save, so without this a failed write reads exactly like a successful one until a
	 * reload contradicts it.
	 */
	northStarUnsaved: boolean;
	/** Confirm it, or pass null to withdraw it. */
	confirmNorthStar: (star: NorthStar | null) => void;
	/**
	 * Send a free-text answer to be read rather than stored blindly. Resolves to what was made of
	 * it, so the card knows whether it is moving on or staying for a follow-up — and what to offer.
	 */
	submitAnswer: (
		id: GroundingFieldId,
		value: string,
	) => Promise<GroundingReply>;
	commit: (id: GroundingFieldId, value: string) => void;
	/** The number, read from whatever the owner typed. Asks again when it holds no number. */
	submitNorthStar: (value: string) => Promise<GroundingReply>;
	/** The number, picked from the answers we offered. */
	commitNorthStar: (value: string) => void;
	/**
	 * An answer corrected on the summary. It is not a turn in the conversation, so it is not said
	 * into the transcript, and it must not move the step cursor.
	 */
	commitFromReview: (id: GroundingFieldId, value: string) => void;
	/** Record the in-progress draft so flush() can save it without a commit. */
	noteDraft: (id: GroundingFieldId, value: string) => void;
	/** Where a submitted step goes: the next question the owner has not answered. */
	advanceAfterCommit: () => void;
	/** Leave the current step without saving it. */
	cancelStep: () => void;
	next: () => void;
	back: () => void;
	releaseExiting: () => void;
	retrySave: () => void;
	flush: () => Promise<string | null>;
}

const EMPTY_REPLY: GroundingReply = {
	verdict: 'answered',
	facts: {},
	reply: '',
	suggestions: [],
	judged: false,
};

export const EXIT_MS = 180;

export const useGroundingFlow = (
	businessId: string,
	label: string,
	strategySeed?: StrategySeed,
): GroundingFlowState => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();

	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [saveState, setSaveState] = useState<SaveState>('idle');
	const [answers, setAnswers] = useState<GroundingAnswers>({});
	const [seedAnswers, setSeedAnswers] = useState<GroundingAnswers>({});
	const [extra, setExtra] = useState('');
	const [preserveOriginalFirst, setPreserveOriginalFirst] = useState(false);
	// The walk starts at question one and the summary is the REWARD at the end, not the lobby.
	// There is no way back into the walk from the summary: every answer is corrected in place there.
	const [stepIndex, setStepIndex] = useState<StepIndex>(0);
	// Counts cursor MOVES. Arriving at a step is what makes a turn, not the step's identity — see
	// the turn effect, which cannot dedup on `field:step` alone once going back is possible.
	const [visit, setVisit] = useState(0);
	// Read synchronously by in-flight work that has to know whether the owner has moved since it
	// started; state would only tell it what was true when its closure was made.
	const visitRef = useRef(0);
	const [direction, setDirection] = useState<'forward' | 'back'>('forward');
	const [exitingStep, setExitingStep] = useState<StepIndex | null>(null);
	const [conversation, setConversation] = useState<ConversationEntry[]>([]);
	const [replyPending, setReplyPending] = useState(false);
	// What they confirmed, or null when nothing is set yet. Held in state so the summary row can
	// read it back without re-parsing the stored wire value on every render.
	const [northStar, setNorthStar] = useState<NorthStar | null>(null);
	// The number is not one of `answers` — it lives in corrections under its own keys — so the
	// turn effect cannot read it the way it reads every other value on file.
	const northStarRef = useRef<NorthStar | null>(null);
	northStarRef.current = northStar;
	// Whether the confirmation on screen is one the server has taken. Its own flag rather than a
	// read of `saveState`, which is shared with the nine answers: an unrelated answer failing must
	// not put a warning on a number that saved, and a later answer saving must not clear one.
	const [northStarUnsaved, setNorthStarUnsaved] = useState(false);
	// Follow-ups spent per fact. The cap lives here rather than on the server because the server
	// handles one answer at a time and cannot know how many times we have already come back.
	const followUpsRef = useRef<Partial<Record<GroundingFieldId, number>>>({});
	// Facts the owner answered in THIS conversation. Only these are skipped when advancing — a
	// value we inferred is the thing we want them to look at, not a reason to stay silent.
	//
	// Mirrored into state because it is also the only honest measure of progress. A count of
	// FILLED fields cannot be: every field arrives pre-filled with our own guesses, so it reads
	// "done" before a single question has been asked. The ref stays because advancing and
	// persisting both read it synchronously, mid-commit, before a re-render could land.
	const answeredByOwnerRef = useRef<Set<GroundingCorrectionId>>(new Set());
	const [answeredByOwner, setAnsweredByOwner] = useState(0);
	// The ref is also what opens the persist gate, so it holds every id the owner acted on. The
	// COUNT answers "how many of the questions have you checked?", and the number is now one of
	// them — it is asked as a step of the walk, so it counts (ADO-1963).
	const publishAnswered = useCallback(() => {
		const fields = GROUNDING_FIELDS.filter((f) =>
			answeredByOwnerRef.current.has(f.id),
		).length;
		const star = answeredByOwnerRef.current.has(NORTH_STAR_VALUE_KEY) ? 1 : 0;
		setAnsweredByOwner(fields + star);
	}, []);
	const markAnswered = useCallback(
		(...ids: GroundingCorrectionId[]) => {
			const fresh = ids.filter((id) => !answeredByOwnerRef.current.has(id));
			if (!fresh.length) return;
			for (const id of fresh) answeredByOwnerRef.current.add(id);
			publishAnswered();
		},
		[publishAnswered],
	);
	const entrySeqRef = useRef(0);

	// Going back REWINDS. The transcript is append-only, so without this a step back left every
	// message on screen and appended the re-asked question to the end — the cursor moved but the
	// screen did not, which is why Back read as doing nothing at all (and as doing nothing twice,
	// mid-"thinking", where the composing indicator is the only thing on screen either way).
	//
	// Rewinding also has to un-answer the facts it drops. They stay in `answers` as values, but a
	// fact whose question is no longer in the transcript must be ASKED again, or walking forward
	// would skip it and leave a hole where the conversation used to be.
	const rewindTo = useCallback(
		(index: StepIndex) => {
			const from: GroundingCorrectionId[] = fieldsFromStep(index).map(
				(f) => f.id,
			);
			// Rewinding past the number un-asks it too, or walking forward again would step over it.
			//
			// BOTH of its keys. The QUESTION is recorded under the field's own id, `northStar`
			// (`say('agent', res.question, field.id)`), and the ANSWER under `northStarValue`.
			// `NORTH_STAR_FIELD` is not a member of `GROUNDING_FIELDS`, so `fieldsFromStep` can
			// never supply the first one. Pushing the answer alone cut BELOW the question and left
			// it on screen, so walking forward asked it again, one line under itself (ADO-1984).
			if (index <= NORTH_STAR_STEP)
				from.push(NORTH_STAR_PROSE_KEY, NORTH_STAR_VALUE_KEY);
			const dropped = new Set<string>(from);
			setConversation((prev) => {
				const cut = prev.findIndex((e) => e.fieldId && dropped.has(e.fieldId));
				return cut === -1 ? prev : prev.slice(0, cut);
			});
			for (const id of from) answeredByOwnerRef.current.delete(id);
			// Follow-ups belong to the interviewer's questions. The number is asked from a fixed
			// vocabulary, so it never has one.
			for (const f of fieldsFromStep(index)) delete followUpsRef.current[f.id];
			publishAnswered();
		},
		[publishAnswered],
	);
	const say = useCallback(
		(
			role: 'agent' | 'owner',
			text: string,
			fieldId?: GroundingCorrectionId,
		) => {
			const trimmed = text.trim();
			if (!trimmed) return;
			entrySeqRef.current += 1;
			setConversation((prev) => [
				...prev,
				{ id: `e${entrySeqRef.current}`, role, text: trimmed, fieldId },
			]);
		},
		[],
	);

	// The last markdown we know the server holds, so an unchanged re-save is skipped.
	const savedRef = useRef<string | null>(null);
	// Generate awaits flush(), so an in-flight per-step PATCH has to be awaited too or the run
	// can read Cosmos before the final write lands.
	const inFlightRef = useRef<Promise<unknown> | null>(null);
	const answersRef = useRef(answers);
	answersRef.current = answers;
	const seedAnswersRef = useRef(seedAnswers);
	seedAnswersRef.current = seedAnswers;
	const pendingRef = useRef<{ id: GroundingFieldId; value: string } | null>(
		null,
	);
	const correctionsRef = useRef<Corrections>({});
	// What the server last confirmed it holds, so a Save that only moved a correction still writes,
	// and one that moved nothing still doesn't.
	const savedCorrectionsRef = useRef<Corrections>({});
	// The goal answer the north star was confirmed against. When that answer changes the
	// confirmation is stale — they told us something new about what they are growing.
	const northStarGoalRef = useRef<string | null>(null);

	// Compare the number on screen against the one the server has acknowledged. Called after every
	// write settles, so a failure raises the warning and the next success takes it back down.
	const syncNorthStarSaved = useCallback(() => {
		const held = correctionsRef.current;
		const saved = savedCorrectionsRef.current;
		setNorthStarUnsaved(
			northStarStamp(
				held[NORTH_STAR_VALUE_KEY] ?? '',
				held[NORTH_STAR_WORDS_KEY] ?? '',
			) !==
				northStarStamp(
					saved[NORTH_STAR_VALUE_KEY] ?? '',
					saved[NORTH_STAR_WORDS_KEY] ?? '',
				),
		);
	}, []);

	// Keyed on (user, business) only. The api client is a dependency in name but not in spirit —
	// re-fetching because an identity-unstable client came back would flip `loading` mid-flow,
	// unmount the stage, and take the owner's in-progress answer with it.
	//
	// Staleness is decided by comparing the key at RESOLUTION time, never by a per-effect
	// `cancelled` flag. StrictMode invokes the effect twice: the first run starts the fetch, its
	// cleanup runs, then the second run sees the key already claimed and returns. A `cancelled`
	// flag set by that cleanup would discard the only in-flight response and leave the flow
	// showing "Loading your business…" forever. The key comparison survives the double-invoke and
	// still drops a genuinely superseded load.
	const loadKeyRef = useRef<string | null>(null);
	useEffect(() => {
		const key = `${userId}:${businessId}`;
		if (loadKeyRef.current === key) return;
		loadKeyRef.current = key;
		setLoading(true);
		void bridgewayApi
			.get<ContextResponse>(
				`/api/user/${userId}/business-context/${businessId}`,
			)
			.then((data) => {
				if (loadKeyRef.current !== key) return;
				const parsed = parseGroundingMarkdown(data.markdown ?? '');
				// A correction the owner made in an earlier session outranks anything we
				// re-inferred this time — that is the whole point of remembering it.
				// Cleaned before anything reads it, so the live set, the saved set and the answers
				// below all agree — a hint healed here must not read as an unsaved change.
				const corrections = withoutHints(data.corrections ?? {});
				correctionsRef.current = { ...corrections };
				savedCorrectionsRef.current = { ...corrections };
				const storedStar = parseNorthStarValue(
					corrections[NORTH_STAR_VALUE_KEY] ?? '',
				);
				// Their own words come back from the key they were written to, and from nowhere
				// else. Falling back to the goal answer here would show them a sentence built out
				// of a different question's answer and call it what they typed.
				setNorthStar(
					storedStar
						? {
								...storedStar,
								ownWords: corrections[NORTH_STAR_WORDS_KEY] ?? '',
							}
						: null,
				);
				northStarGoalRef.current = corrections[NORTH_STAR_VALUE_KEY]
					? (corrections.goal ?? parsed.answers.goal ?? '')
					: null;
				// Only field answers belong in `answers`; the north-star corrections are read from
				// their own keys and would otherwise be composed into the brief as a stray bullet.
				const {
					[NORTH_STAR_PROSE_KEY]: _p,
					[NORTH_STAR_VALUE_KEY]: _v,
					[NORTH_STAR_WORDS_KEY]: _w,
					...fieldCorrections
				} = corrections;
				setAnswers({ ...parsed.answers, ...fieldCorrections });
				// Only an UNSAVED doc is a machine seed. Once the owner has saved, their words are
				// the baseline, and every field already counts as theirs.
				// Corrections are the OWNER's words, so they are never part of the machine
				// baseline — otherwise re-confirming one would look like "no change" forever.
				setSeedAnswers(data.edited ? {} : parsed.answers);
				setExtra(parsed.extra);
				setPreserveOriginalFirst(Boolean(data.edited) && parsed.unstructured);
				savedRef.current = data.edited ? (data.markdown ?? '') : null;
				setLoadError(false);
				setLoading(false);
			})
			.catch((err) => {
				if (loadKeyRef.current !== key) return;
				logger.error('Failed to load business context', err);
				setLoadError(true);
				setLoading(false);
			});
	}, [bridgewayApi, userId, businessId]);

	// Ask the server to compose the turn for the question now on screen. Keyed by step so a
	// slow turn that resolves after the owner has moved on is dropped rather than shown late.
	const [turn, setTurn] = useState<InterviewTurn | null>(null);
	const [turnLoading, setTurnLoading] = useState(false);
	// The question actually asked for each fact. The judge needs it to know what the owner was
	// responding to, and it is never rendered from here — the conversation log is what the owner
	// sees — so a ref is enough.
	const askedByIdRef = useRef<Record<string, string>>({});
	const turnKeyRef = useRef('');
	useEffect(() => {
		if (loading || stepIndex >= REVIEW_STEP) {
			setTurn(null);
			return;
		}
		const field = fieldForStep(stepIndex);
		if (!field) return;
		// Keyed by ARRIVAL, not by step identity. The summary branch above returns WITHOUT clearing
		// turnKeyRef, so a `${field.id}:${stepIndex}` key survives a trip through the summary —
		// editing the SAME fact a second time then matched the stale key and returned without
		// asking, leaving turnLoading false and no turn: a card with no question and no choices.
		//
		// `visit` counts cursor MOVES, not effect runs. StrictMode invokes this twice on mount with
		// the same visit, so the guard below still collapses the double-invoke into one request —
		// bumping a ref here instead would fire two and say() the question twice.
		const key = `${field.id}:${stepIndex}:${visit}`;
		if (turnKeyRef.current === key) return;
		turnKeyRef.current = key;
		setTurn(null);
		setTurnLoading(true);
		const history = fieldsBeforeStep(stepIndex)
			.filter((f) => (answersRef.current[f.id] ?? '').trim())
			.map((f) => ({
				question: f.question,
				answer: answersRef.current[f.id] ?? '',
			}));
		void bridgewayApi
			.post<InterviewTurn>(
				`/api/user/${userId}/business-context/${businessId}/interview-turn`,
				{
					field_id: field.id,
					question: field.question,
					helper: field.helper,
					// The value on file, so the server hoists it to slot 0 and it is badged Keep —
					// the same contract every other question has.
					known_value: isNorthStarStep(stepIndex)
						? northStarRef.current
							? formatNorthStar(northStarRef.current)
							: ''
						: (answersRef.current[field.id] ?? ''),
					history,
				},
			)
			.then((res) => {
				if (turnKeyRef.current !== key) return;
				setTurn(res);
				askedByIdRef.current[field.id] = res.question;
				say('agent', res.question, field.id);
				setTurnLoading(false);
			})
			.catch(() => {
				if (turnKeyRef.current !== key) return;
				// Never block on the interviewer: fall back to our own question and whatever
				// value we already hold, which is exactly what the server would have returned.
				const known = (answersRef.current[field.id] ?? '').trim();
				setTurn({
					question: field.question,
					suggestions: known ? [known] : [],
					grounded: false,
				});
				askedByIdRef.current[field.id] = field.question;
				say('agent', field.question, field.id);
				setTurnLoading(false);
			});
	}, [loading, stepIndex, visit, bridgewayApi, userId, businessId, say]);

	const compose = useCallback(
		(next: GroundingAnswers) =>
			composeGroundingMarkdown({
				answers: next,
				label,
				extra,
				preserveOriginalFirst,
			}),
		[label, extra, preserveOriginalFirst],
	);

	// A field is dirty only when it differs from what we inferred. Agreeing with every guess is
	// not an edit, and must not flip the server's `edited` flag.
	const dirty = useMemo(
		() =>
			GROUNDING_FIELDS.some(
				(f) => (answers[f.id] ?? '') !== (seedAnswers[f.id] ?? ''),
			),
		[answers, seedAnswers],
	);

	const persist = useCallback(
		async (next: GroundingAnswers): Promise<string | null> => {
			// A confirmation is about a particular goal. Once that answer changes, the number they
			// confirmed is about something they no longer said, so it is dropped rather than left to
			// outrank a fresh reading. Done here because every writer funnels through persist —
			// the suggestion path, the judge (which can fill `goal` from an answer to a different
			// question), the Generate pre-flight and the retry all end up on this line.
			if (
				northStarGoalRef.current !== null &&
				(next.goal ?? '') !== northStarGoalRef.current
			) {
				correctionsRef.current = {
					...correctionsRef.current,
					[NORTH_STAR_PROSE_KEY]: '',
					[NORTH_STAR_VALUE_KEY]: '',
					[NORTH_STAR_WORDS_KEY]: '',
				};
				northStarGoalRef.current = null;
				answeredByOwnerRef.current.delete(NORTH_STAR_VALUE_KEY);
				setNorthStar(null);
			}
			// The north star is confirmed on the summary, not answered as one of the nine questions,
			// so it moves `corrections` without moving any answer. Without this the gates below
			// would send it straight back out the door — a Save that writes nothing at all.
			const correctionsChanged = !sameCorrections(
				correctionsRef.current,
				savedCorrectionsRef.current,
			);
			const changed = GROUNDING_FIELDS.some(
				(f) => (next[f.id] ?? '') !== (seedAnswers[f.id] ?? ''),
			);
			if (!changed && !correctionsChanged) return savedRef.current;
			// Nothing the owner did this session touched an answer, so there is nothing to write.
			// Once a doc is saved `seedAnswers` is empty (their words are the baseline), which makes
			// `changed` true on load alone — and re-composing a brief written in the old free-prose
			// editor moves its paragraphs below the bullets they explain. Opening the panel must
			// leave the saved doc exactly as the owner left it.
			//
			// Moving a correction counts as the owner acting, which is not the same as answering a
			// question: withdrawing a confirmation REMOVES its entry from that set, so gating on the
			// set alone would silently refuse to write the withdrawal and leave the old number
			// standing as though they had never taken it back.
			if (!answeredByOwnerRef.current.size && !correctionsChanged) {
				return savedRef.current;
			}
			const markdown = compose(next);
			if (markdown === savedRef.current && !correctionsChanged) return markdown;
			setSaveState('saving');
			// Reads corrections when the request actually goes out, never when it is queued: a
			// snapshot taken at queue time would re-send a value a later write already cleared.
			const send = () =>
				bridgewayApi
					.patch(`/api/user/${userId}/business-context/${businessId}`, {
						markdown,
						corrections: correctionsRef.current,
					})
					.then(() => {
						savedRef.current = markdown;
						savedCorrectionsRef.current = { ...correctionsRef.current };
						setSaveState('saved');
						syncNorthStarSaved();
						return markdown;
					})
					.catch((err) => {
						logger.error('Failed to save business context', err);
						// Leave savedRef alone so the next commit retries this content rather than
						// orphaning every answer written after the failure.
						setSaveState('error');
						syncNorthStarSaved();
						return null;
					});
			// Queue behind any write still in flight. The doc is replaced wholesale server-side with
			// no etag, so two overlapping PATCHes mean the slower one silently wins — and confirming
			// on the same screen that hosts Generate makes that overlap the normal path, not an edge
			// case. The leading catch keeps a failed write from cancelling the one behind it.
			const request = (inFlightRef.current ?? Promise.resolve())
				.catch(() => null)
				.then(send);
			inFlightRef.current = request;
			return request;
		},
		[
			bridgewayApi,
			userId,
			businessId,
			compose,
			seedAnswers,
			syncNorthStarSaved,
		],
	);

	// When a propose_new_strategy recommendation is approved, the create view opens carrying a
	// seed of what the agent suggested, so the owner reviews a filled-in brief rather than a
	// blank one.
	//
	// The suggestion goes into BOTH `answers` and `seedAnswers` — shown in the field, and
	// recorded as the baseline we inferred. That is what keeps it out of the owner's mouth:
	// `persist` writes only when an answer differs from its baseline, so an untouched
	// suggestion sends nothing to `/business-context`, and abandoning creation leaves the
	// canonical context exactly as it was. Editing the field makes it differ from the baseline,
	// and the owner's own edit is what carries it to the server.
	//
	// Deliberately NOT marked answered: `answeredByOwnerRef` means "the owner said this", and
	// it drives both the progress count and which questions the walk still asks. A suggestion
	// they have not read yet is the thing we most want them to look at.
	const seedAppliedRef = useRef(false);
	useEffect(() => {
		if (loading || !strategySeed || seedAppliedRef.current) return;

		const map: Partial<Record<GroundingFieldId, string>> = {};
		if (strategySeed.recommended_focus)
			map.goal = strategySeed.recommended_focus;
		if (strategySeed.channel_suggestion)
			map.howWeSell = strategySeed.channel_suggestion;

		if (!Object.keys(map).length) return;

		// Only latch once we know there was something to apply, so a seed that arrives empty does
		// not burn the one-shot and block a later populated one.
		seedAppliedRef.current = true;
		answersRef.current = { ...answersRef.current, ...map };
		setAnswers({ ...answersRef.current });
		setSeedAnswers((prev) => ({ ...prev, ...map }));
	}, [loading, strategySeed]);

	// Picking one of the offered answers. It needs no judging — we wrote it — so it commits
	// directly, but it is still something the owner said and belongs in the transcript.
	const commit = useCallback(
		(id: GroundingFieldId, value: string) => {
			pendingRef.current = null;
			// A hint can still reach here from a model that echoed the seed back at us, so it is
			// refused at the write boundary too, not only when a stored one is read.
			const trimmed = stripHintFromAnswer(value);
			// Nothing left once the hint comes off means the answer carried no fact. `commitMany`
			// refuses one the same way. Writing it here put an EMPTY string over whatever the brief
			// already held for this field and persisted that, so tapping an offer that echoed the
			// seed deleted the fact it was meant to confirm.
			if (!trimmed) return;
			say('owner', trimmed, id);
			// Remember it only when it differs from what we inferred — echoing our own guess
			// back as a "correction" would teach the next run nothing.
			if (trimmed && trimmed !== (seedAnswersRef.current[id] ?? '')) {
				correctionsRef.current = { ...correctionsRef.current, [id]: trimmed };
			}
			if (trimmed) markAnswered(id);
			const next = { ...answersRef.current, [id]: trimmed };
			answersRef.current = next;
			setAnswers(next);
			void persist(next);
		},
		[persist, say, markAnswered],
	);

	// The north star the owner confirmed on the summary. Not a commit: it is not one of the nine
	// questions, it does not belong in the transcript, and it must not advance the walk.
	const confirmNorthStar = useCallback(
		(star: NorthStar | null) => {
			correctionsRef.current = {
				...correctionsRef.current,
				[NORTH_STAR_PROSE_KEY]: star ? formatNorthStar(star) : '',
				[NORTH_STAR_VALUE_KEY]: star ? toNorthStarValue(star) : '',
				[NORTH_STAR_WORDS_KEY]: star?.ownWords?.trim() ?? '',
			};
			if (star) {
				// Remember which goal answer this was confirmed against, so a later change to what
				// they are growing retires it (see persist).
				northStarGoalRef.current = answersRef.current.goal ?? '';
				markAnswered(NORTH_STAR_VALUE_KEY);
			} else {
				northStarGoalRef.current = null;
				answeredByOwnerRef.current.delete(NORTH_STAR_VALUE_KEY);
				publishAnswered();
			}
			setNorthStar(star);
			void persist(answersRef.current);
		},
		[persist, markAnswered, publishAnswered],
	);

	/**
	 * The number, chosen from the answers we offered.
	 *
	 * Mirrors `commit`: it SAYS what the owner picked before it stores it. Without that the
	 * transcript jumps from the question straight to the next one, and the owner cannot see the
	 * answer they just gave.
	 */
	const commitNorthStar = useCallback(
		(value: string) => {
			const text = value.trim();
			if (!text) return;
			say('owner', text, NORTH_STAR_VALUE_KEY);
			const star = parseNorthStarAnswer(text);
			// Only a figure is stored, and a chip that carries none leaves the number alone. That
			// matches `submitNorthStar` below, whose decline and unclear branches both keep it:
			// `confirmNorthStar(null)` means the owner REMOVED their number on the summary, which
			// is not what picking a plain-language chip says. Clearing here would also blank
			// `northStarWords`, so their answer would be stored nowhere at all.
			//
			// The period rule is read here for the same reason `submitNorthStar` reads it: a figure
			// that counts TIME is a date, never the target. A chip is model-written free text, so
			// "ask me in 3 months" arrives on this path too, and it used to be stored — with
			// `markAnswered`, so the interview never asked again. One set of words must not mean
			// two different things, decided only by chip versus composer.
			if (star && !figureNamesAPeriod(text)) confirmNorthStar(star);
		},
		[say, confirmNorthStar],
	);

	/**
	 * The number, answered in the owner's own words.
	 *
	 * It is asked like every other question — prose, then the composer — so it is READ rather than
	 * validated. A number we can find is stored; "not sure yet" moves on; anything else is asked
	 * again in our own voice, which is what a person would do. A validation error on a chat turn
	 * is not a conversation.
	 */
	const submitNorthStar = useCallback(
		async (value: string): Promise<GroundingReply> => {
			const text = value.trim();
			if (!text) return EMPTY_REPLY;
			say('owner', text, NORTH_STAR_VALUE_KEY);

			// READ FIRST, and treat a decline as the fallback. `DECLINES` is an anchored PREFIX
			// match, so "not sure, maybe 500 subscribers" matched the hedge however the sentence
			// carried on, and a usable number was thrown away (ADO-1984). A hedge in front of a
			// figure is still an answer.
			// Reading first opened the opposite case. A figure that counts a PERIOD is not a goal:
			// "not sure yet, ask me in 3 months" names a DATE, and it was stored as a north star
			// of 3. That rule is absolute, so it stands on its own rather than behind a decline —
			// "ask me in 3 months" carries no hedge and stored the 3 the same way.
			//
			// The separator is what the figure COUNTS, not whether we recognise the thing counted.
			// An off-vocabulary count is still a goal — "not sure, maybe 300 gift boxes" means 300
			// boxes — so testing for OUTCOME_OTHER here would throw away every owner counting
			// something outside the eight measures, which is the defect reading first just fixed.
			// A period is the one thing a figure can count that is never the target.
			const star = parseNorthStarAnswer(text);
			if (star && !figureNamesAPeriod(text)) {
				confirmNorthStar(star);
				return { ...EMPTY_REPLY, verdict: 'answered' };
			}

			// No number anywhere in the answer. That is the case the decline list is for.
			if (declinesNorthStar(text)) {
				const reply = "That's fine — we can put a number on it later.";
				say('agent', reply);
				return { ...EMPTY_REPLY, verdict: 'declined', reply };
			}

			const reply =
				'I need a number to aim at — something like "500 subscribers" or "$50,000 in revenue".';
			say('agent', reply);
			return { ...EMPTY_REPLY, verdict: 'unclear', reply };
		},
		[say, confirmNorthStar],
	);

	// Several facts from one answer, in one write. Committing them one at a time would fire a
	// PATCH per fact and race them against each other, since each starts from the answers it
	// captured rather than the merged result.
	const commitMany = useCallback(
		(facts: Partial<Record<GroundingFieldId, string>>) => {
			pendingRef.current = null;
			const next = { ...answersRef.current };
			const corrections = { ...correctionsRef.current };
			const filled: GroundingFieldId[] = [];
			for (const [key, raw] of Object.entries(facts)) {
				const id = key as GroundingFieldId;
				// These facts come back from the judge, which is shown the seed brief — hints and
				// all — so it can hand one back as though the owner had said it.
				const trimmed = stripHintFromAnswer(raw ?? '');
				if (!trimmed) continue;
				if (trimmed !== (seedAnswersRef.current[id] ?? ''))
					corrections[id] = trimmed;
				filled.push(id);
				next[id] = trimmed;
			}
			markAnswered(...filled);
			correctionsRef.current = corrections;
			answersRef.current = next;
			setAnswers(next);
			void persist(next);
		},
		[persist, markAnswered],
	);

	// An answer the owner corrected on the summary. It routes through commitMany, which does
	// everything commit does except say() the value into the transcript — the same rule
	// confirmNorthStar follows. The owner is not taking a turn in the interview, so the interview
	// log must not grow an entry with no question above it.
	//
	// commitMany also clears pendingRef. That is safe today, because every route from a step card
	// back to the summary already clears it. Do not wire `next` or `back` to a control without
	// revisiting this.
	const commitFromReview = useCallback(
		(id: GroundingFieldId, value: string) => commitMany({ [id]: value }),
		[commitMany],
	);

	// A typed answer is READ before it is stored. This is the whole point of the redesign: the
	// owner is in a conversation, so what they send is not guaranteed to be the fact we asked for.
	// It may answer a different question, answer several at once, ask us something, or be a shrug.
	//
	// Fail-open in the same shape as the server: any error here takes them at their word, which is
	// exactly the behavior this replaced. A broken judge must never lose someone's answer.
	const submitAnswer = useCallback(
		async (id: GroundingFieldId, value: string): Promise<GroundingReply> => {
			const trimmed = value.trim();
			const taken: GroundingReply = {
				verdict: 'answered',
				facts: {},
				reply: '',
				suggestions: [],
				judged: false,
			};
			if (!trimmed) return taken;
			pendingRef.current = null;
			say('owner', trimmed, id);
			setReplyPending(true);
			const field = GROUNDING_FIELDS.find((f) => f.id === id);
			const asked = askedByIdRef.current[id] ?? field?.question ?? '';
			// Where the owner was when they sent this. Going back while the judge is still reading
			// abandons the answer: committing it would write a fact for a question no longer on
			// screen, and its 'answered' verdict would advance the cursor off the step they chose.
			const sentAt = visitRef.current;
			const abandoned = () => visitRef.current !== sentAt;
			try {
				const res = await bridgewayApi.post<GroundingReply>(
					`/api/user/${userId}/business-context/${businessId}/reply`,
					{
						field_id: id,
						field_label: field?.label ?? id,
						question: asked,
						answer: trimmed,
						history: GROUNDING_FIELDS.filter(
							(f) => f.id !== id && (answersRef.current[f.id] ?? '').trim(),
						).map((f) => ({
							question: askedByIdRef.current[f.id] ?? f.question,
							answer: answersRef.current[f.id] ?? '',
						})),
						remaining: GROUNDING_FIELDS.filter(
							(f) => !(answersRef.current[f.id] ?? '').trim(),
						).map((f) => ({ id: f.id, label: f.label })),
						field_ids: GROUNDING_FIELDS.map((f) => f.id),
						follow_ups_used: followUpsRef.current[id] ?? 0,
						// So the follow-up does not offer back the value we already hold as if it
						// were a new suggestion.
						known_value: answersRef.current[id] ?? '',
					},
				);
				// Left this step while we were reading. Say nothing, write nothing, and report a
				// follow-up so the caller holds position instead of advancing off where they went.
				if (abandoned()) return { ...taken, abandoned: true };
				if (res.reply) say('agent', res.reply, id);
				if (Object.keys(res.facts ?? {}).length) commitMany(res.facts);
				if (res.verdict === 'question' || res.verdict === 'unclear') {
					followUpsRef.current[id] = (followUpsRef.current[id] ?? 0) + 1;
				}
				return res;
			} catch (err) {
				logger.error('Failed to read the answer', err);
				if (abandoned()) return { ...taken, abandoned: true };
				commitMany({ [id]: trimmed });
				return taken;
			} finally {
				setReplyPending(false);
			}
		},
		[bridgewayApi, userId, businessId, commitMany, say],
	);

	// What the owner has typed into the current card but not yet committed. Kept in a ref so
	// keystrokes don't re-render the stage, and merged by flush() — Generate must not discard the
	// answer someone was in the middle of writing when they clicked it.
	const noteDraft = useCallback((id: GroundingFieldId, value: string) => {
		pendingRef.current = { id, value };
	}, []);

	// The cursor is read through a ref so move() never depends on the current index — otherwise
	// every advance rebuilds the callbacks and the step card, which is mid-animation, remounts.
	const stepRef = useRef(stepIndex);
	stepRef.current = stepIndex;

	const move = useCallback((to: StepIndex, dir: 'forward' | 'back') => {
		const from = stepRef.current;
		if (from === to) return;
		setDirection(dir);
		// Exactly one card may be exiting. A fast triple-Enter replaces the held card rather
		// than stacking, so no node is left painted with an animationend nothing listens for.
		setExitingStep(from);
		stepRef.current = to;
		setStepIndex(to);
		// A new arrival, so the turn effect asks again even when `to` is a step already visited.
		visitRef.current += 1;
		setVisit(visitRef.current);
	}, []);

	const advanceAfterCommit = useCallback(() => {
		// Never ask about a fact THEY already gave us. One answer can fill several, so asking
		// "anything new you're launching?" right after someone volunteered their September launch
		// is the thing that makes this feel like a form that wasn't listening.
		//
		// Only what the owner told us counts. A fact we pre-filled from our own research is
		// exactly what this interview exists to put in front of them, so those are still asked.
		let to = stepRef.current + 1;
		while (to < REVIEW_STEP && answeredByOwnerRef.current.has(stepId(to))) {
			to += 1;
		}
		move(Math.min(to, REVIEW_STEP), 'forward');
	}, [move]);

	// Cancel discards nothing already saved; it just leaves this step. It is a plain step back.
	//
	// The first question has nowhere to go back to. The summary is the reward at the END of the
	// walk, so leaving step one used to hand the owner a summary of answers they had not yet been
	// asked about — the way out of the walk is the panel's own Back, which leaves the create view.
	//
	// Backing out has to drop the uncommitted draft as well. It is held for flush() to rescue on
	// Generate, but an answer the owner explicitly walked away from is not one they were in the
	// middle of writing — resurrecting it would put words they rejected in front of the strategist.
	const cancelStep = useCallback(() => {
		// A no-op, draft included: the card keeps showing what they typed, so dropping the copy
		// flush() would rescue would silently un-save text still on screen.
		if (stepRef.current === 0) return;
		pendingRef.current = null;
		const to = stepRef.current - 1;
		rewindTo(to);
		move(to, 'back');
	}, [move, rewindTo]);
	const next = useCallback(
		() => move(Math.min(stepRef.current + 1, REVIEW_STEP), 'forward'),
		[move],
	);
	const back = useCallback(
		() => move(Math.max(stepRef.current - 1, 0), 'back'),
		[move],
	);
	const releaseExiting = useCallback(() => setExitingStep(null), []);

	const retrySave = useCallback(() => {
		void persist(answersRef.current);
	}, [persist]);

	// The generate pre-flight: commit whatever is typed but uncommitted, and make sure any
	// per-step write already in flight has landed before the run reads the doc.
	const flush = useCallback(async (): Promise<string | null> => {
		const pending = pendingRef.current;
		const merged = pending
			? { ...answersRef.current, [pending.id]: pending.value.trim() }
			: answersRef.current;
		if (pending) {
			answersRef.current = merged;
			pendingRef.current = null;
			// Typed but never submitted is still the owner answering, and persist only writes for
			// fields they touched — without this the rescued draft would be dropped, not saved.
			if (pending.value.trim()) markAnswered(pending.id);
			setAnswers(merged);
		}
		const result = await persist(merged);
		await inFlightRef.current?.catch(() => null);
		return result;
	}, [persist, markAnswered]);

	return {
		loading,
		loadError,
		saveState,
		answers,
		seedAnswers,
		stepIndex,
		direction,
		exitingStep,
		extra,
		dirty,
		answeredByOwner,
		turn,
		turnLoading,
		conversation,
		replyPending,
		northStar,
		northStarUnsaved,
		confirmNorthStar,
		submitNorthStar,
		commitNorthStar,
		submitAnswer,
		commit,
		commitFromReview,
		noteDraft,
		advanceAfterCommit,
		cancelStep,
		next,
		back,
		releaseExiting,
		retrySave,
		flush,
	};
};
